import { useCallback, useEffect, useRef, useState, type ChangeEventHandler } from 'react';
import { Input, Message, Modal, Select } from '@arco-design/web-react';
import Apis from '@/app/index';
import { AIGC_PROXY_HOST } from '@/config';
import {
  CompanionDraft,
  CompanionProfile,
  PortraitFrames,
  isPresetCompanion,
} from '@/lib/useCompanions';
import { VOICE_LIST } from '@/lib/voices';
import {
  normalizeFrameToUniformCanvas,
  resolveAvatarRemovalResult,
  colorKeyOrFallback,
  pickMatteKeyColor,
  type MatteKeyColor,
} from '@/utils/avatarMatting';
import styles from './index.module.less';

// The avatar upload runs through several sequential backend steps. We surface
// each one so the user knows why the wait is long instead of seeing a single
// opaque "上传中".
type AvatarStage = 'idle' | 'removing' | 'uploading' | 'generating';

// Avatar frames are produced by a SINGLE pipeline: Ark's server-side semantic
// cutout regularizes the framing (it understands the scene and can drop a stool
// the subject sits on), Ark then generates the three frames onto a UNIFORM
// high-contrast solid plate (green or magenta — whichever the subject's own
// colors are farthest from, chosen by pickMatteKeyColor and echoed back by the
// server), and finally a deterministic client-side color-key lifts that plate to
// transparency (see matteGeneratedFrame → colorKeyOrFallback). The old
// imgly-first and 'auto' classifier pipelines were removed — imgly-first matting
// was too unstable (it kept whole frames opaque on hard photos, and its ISNet
// matte silently ate high-key white outfits). imgly no longer runs at all.

const AVATAR_STAGE_LABEL: Record<Exclude<AvatarStage, 'idle'>, string> = {
  removing: '抠图中...',
  uploading: '上传中...',
  generating: '生成动画中...',
};

function createDraft(profile?: CompanionProfile | CompanionDraft | null): CompanionDraft {
  return {
    name: profile?.name || '',
    userCallName: profile?.userCallName || '',
    personality: profile?.personality || '',
    avatar: profile?.avatar || '',
    portraitFrames: profile?.portraitFrames,
    background: profile?.background || '',
    backgroundVideo: profile?.backgroundVideo || '',
    voiceType: profile?.voiceType || VOICE_LIST[0].value,
  };
}

function readImageAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function blobToDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function isInlineAsset(value: string) {
  return value.startsWith('data:');
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function resizeImageDataUrl(
  src: string,
  {
    maxEdge,
    mimeType,
    quality,
  }: {
    maxEdge: number;
    mimeType: string;
    quality?: number;
  }
) {
  const image = await loadImage(src);
  const scale = Math.min(1, maxEdge / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return src;
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL(mimeType, quality);
}

async function uploadImageAsset(
  image: string,
  usage: 'avatar' | 'background' | 'background-video' | 'frame',
  signal?: AbortSignal
) {
  const response = (await Apis.Basic.uploadAsset(
    {
      SceneID: 'Custom',
      image,
      usage,
    },
    signal
  )) as {
    url?: string;
  };
  if (!response?.url) {
    throw new Error('upload asset failed');
  }
  return response.url;
}

async function fetchFrameUrlAsDataUrl(url: string, signal?: AbortSignal) {
  const proxyUrl = `${AIGC_PROXY_HOST || ''}/image-proxy?url=${encodeURIComponent(url)}`;
  const response = await fetch(proxyUrl, { signal });
  if (!response.ok) {
    throw new Error(`fetch frame failed: ${response.status}`);
  }
  return blobToDataUrl(await response.blob());
}

// Ark may also return a redrawn neutral alongside the two expression frames
// (see `includeNeutral`), so the raw response can carry a `neutral` field. It
// also echoes back the `bgColor` plate it painted so the client keys the exact
// color out.
type GeneratedFrames = PortraitFrames & { neutral?: string; bgColor?: MatteKeyColor };

function unwrapGeneratedFrames(raw: unknown): GeneratedFrames | undefined {
  const source =
    raw && typeof raw === 'object' && 'Result' in raw ? (raw as { Result?: unknown }).Result : raw;
  if (!source || typeof source !== 'object') return undefined;
  return source as GeneratedFrames;
}

async function requestGeneratedFrameDataUrls(
  image: string,
  includeNeutral: boolean,
  bgColor: MatteKeyColor,
  model: string,
  signal?: AbortSignal
) {
  try {
    return (await Apis.Basic.generateCompanionFrames(
      {
        SceneID: 'Custom',
        image,
        includeNeutral,
        bgColor,
        model,
      },
      signal
    )) as GeneratedFrames | undefined;
  } catch (error) {
    // A user-initiated cancel must not silently fall through to the proxy retry.
    if ((error as { name?: string })?.name === 'AbortError' || signal?.aborted) throw error;
    // Fall through to the proxy action below.
  }

  const proxyUrl = `${AIGC_PROXY_HOST || ''}/proxy?Action=generateCompanionFrames`;
  const response = await fetch(proxyUrl, {
    method: 'post',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      SceneID: 'Custom',
      image,
      includeNeutral,
      bgColor,
      model,
    }),
    signal,
  });
  const text = await response.text();
  let payload: unknown;
  try {
    payload = JSON.parse(text);
  } catch {
    throw new Error(`proxy generateCompanionFrames returned non-json: ${text.slice(0, 120)}`);
  }
  if (!response.ok) {
    throw new Error(`proxy generateCompanionFrames failed: ${response.status}`);
  }
  const metadata = (payload as { ResponseMetadata?: { Error?: { Message?: string } } })
    ?.ResponseMetadata;
  if (metadata?.Error) {
    throw new Error(metadata.Error.Message || 'proxy generateCompanionFrames failed');
  }
  return unwrapGeneratedFrames(payload);
}

// The generated frames arrive on a UNIFORM high-contrast solid plate (green or
// magenta, echoed back as `bgColor`). We lift that plate with a deterministic
// client-side color-key: unlike the old imgly ISNet matte, a color-key can't
// silently eat a high-key white outfit (its decision is per-pixel hue distance
// from the plate, not a learned foreground mask), and unlike the bare flood-fill
// it reaches background trapped inside the silhouette (arm gaps) without nibbling
// hair/light edges. colorKeyOrFallback still guards: if the plate wasn't
// separable enough it falls back to the flood-fill matte, so edges can only get
// better, never worse.
async function matteGeneratedFrame(src: string, keyColor: MatteKeyColor) {
  return colorKeyOrFallback(src, keyColor);
}

async function normalizeAndUploadGeneratedFrame(
  value: string | undefined,
  keyColor: MatteKeyColor,
  signal?: AbortSignal
) {
  try {
    const asset = typeof value === 'string' ? value.trim() : '';
    if (!asset) return '';
    if (asset.startsWith('data:image/')) {
      const matted = await matteGeneratedFrame(asset, keyColor);
      if (signal?.aborted) return '';
      const transparentPng = await normalizeFrameToUniformCanvas(matted);
      return uploadImageAsset(transparentPng, 'frame', signal);
    }
    const normalized = asset.split(/[?#]/)[0].toLowerCase();
    if (
      asset.startsWith('data:image/png') ||
      asset.startsWith('data:image/webp') ||
      normalized.endsWith('.png') ||
      normalized.endsWith('.webp')
    ) {
      return asset;
    }
    if (/^https?:\/\//i.test(asset)) {
      const proxiedDataUrl = await fetchFrameUrlAsDataUrl(asset, signal);
      const matted = await matteGeneratedFrame(proxiedDataUrl, keyColor);
      if (signal?.aborted) return '';
      const transparentPng = await normalizeFrameToUniformCanvas(matted);
      return uploadImageAsset(transparentPng, 'frame', signal);
    }
    return '';
  } catch {
    return '';
  }
}

async function generatePortraitFrames(
  includeNeutral: boolean,
  keyColor: MatteKeyColor,
  model: string,
  signal: AbortSignal | undefined,
  ...sources: string[]
) {
  const uniqueSources = Array.from(new Set(sources.map((source) => source.trim()).filter(Boolean)));
  // Every frame (neutral / mouth-open / eyes-closed) is an INDEPENDENT one-gen
  // Ark edit off the same Ark-regularized source (includeNeutral=false). We no
  // longer derive the mouth/eyes from a redrawn neutral — deriving off a redraw
  // is two generations of drift and caused the black-teeth / skewed-mouth
  // artifacts. Every frame is matted and independently mapped onto the same fixed
  // canvas by normalizeFrameToUniformCanvas, so no cross-frame anchor is needed.
  // The normalized neutral becomes the new avatar.
  const trySource = async (index: number): Promise<GeneratedFrames | undefined> => {
    if (signal?.aborted) return undefined;
    const image = uniqueSources[index];
    if (!image) return undefined;
    try {
      const response = await requestGeneratedFrameDataUrls(
        image,
        includeNeutral,
        keyColor,
        model,
        signal
      );
      if (signal?.aborted) return undefined;
      const frames = unwrapGeneratedFrames(response);
      // Key the exact plate the server reported it painted (fall back to the
      // color we asked for if the response omitted it).
      const frameKeyColor = frames?.bgColor || keyColor;
      // Every frame — neutral included — is matted then mapped onto the SAME
      // fixed canvas by normalizeFrameToUniformCanvas using ITS OWN bounding box
      // pinned to the same target height + top margin, so all three end up with
      // an identical subject height and head-top position (no shared box, no
      // anchor). The normalized neutral becomes the returned avatar so the static
      // portrait shares that exact placement with the animation frames.
      let neutral = '';
      if (frames?.neutral) {
        const mattedNeutral = await matteGeneratedFrame(frames.neutral, frameKeyColor);
        if (signal?.aborted) return undefined;
        const normalizedNeutral = await normalizeFrameToUniformCanvas(mattedNeutral);
        neutral = await uploadImageAsset(normalizedNeutral, 'avatar', signal);
      }
      const [mouthOpen, eyesClosed] = await Promise.all([
        normalizeAndUploadGeneratedFrame(frames?.mouthOpen, frameKeyColor, signal),
        normalizeAndUploadGeneratedFrame(frames?.eyesClosed, frameKeyColor, signal),
      ]);
      if (signal?.aborted) return undefined;
      if (mouthOpen || eyesClosed) {
        return { neutral, mouthOpen, eyesClosed };
      }
    } catch (error) {
      if ((error as { name?: string })?.name === 'AbortError' || signal?.aborted) throw error;
      // Try the next source. The uploaded URL points to the same background-removed PNG.
    }
    return trySource(index + 1);
  };

  return trySource(0);
}

export default function CompanionSettingsModal(props: {
  visible: boolean;
  profile?: CompanionProfile | null;
  submitting?: boolean;
  onCancel: () => void;
  onSubmit: (draft: CompanionDraft, editingId?: string) => void | Promise<void>;
}) {
  const { visible, profile, submitting = false, onCancel, onSubmit } = props;
  const isPreset = isPresetCompanion(profile);
  const [draft, setDraft] = useState<CompanionDraft>(createDraft(profile));
  const [avatarProcessing, setAvatarProcessing] = useState(false);
  // Tracks which backend step the avatar upload is currently on so we can show a
  // per-step label + hint instead of a single opaque "上传中".
  const [avatarStage, setAvatarStage] = useState<AvatarStage>('idle');
  const [framesGenerating, setFramesGenerating] = useState(false);
  const [backgroundUploading, setBackgroundUploading] = useState(false);
  const [backgroundVideoUploading, setBackgroundVideoUploading] = useState(false);
  const [assetPreparing, setAssetPreparing] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(createDraft(profile).avatar);
  const [backgroundPreview, setBackgroundPreview] = useState(createDraft(profile).background);
  const [backgroundVideoPreview, setBackgroundVideoPreview] = useState(
    createDraft(profile).backgroundVideo || ''
  );
  const [avatarFrameSource, setAvatarFrameSource] = useState('');
  // When a preset avatar is regenerated through the dev-only picker, the result
  // TOS URLs are the deliverable (presets don't persist), so we surface them in
  // a copyable panel to paste back into useCompanions.ts.
  const [, setGeneratedAssetUrls] = useState<{
    avatar: string;
    mouthOpen: string;
    eyesClosed: string;
  } | null>(null);
  const [useProModel, setUseProModel] = useState(false);
  const arkModel = useProModel ? 'pro' : 'flash';
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const bgInputRef = useRef<HTMLInputElement | null>(null);
  const bgVideoInputRef = useRef<HTMLInputElement | null>(null);
  const bgAnyInputRef = useRef<HTMLInputElement | null>(null);
  // Aborts the in-flight avatar pipeline (bg-remove → TOS upload → frame gen) so
  // that clicking 取消 / closing the modal truly stops the work instead of only
  // hiding the window while the async chain keeps running in the background.
  const avatarPipelineAbortRef = useRef<AbortController | null>(null);

  const abortAvatarPipeline = useCallback(() => {
    avatarPipelineAbortRef.current?.abort();
    avatarPipelineAbortRef.current = null;
  }, []);

  // Abort any in-flight pipeline when the modal unmounts.
  useEffect(() => () => abortAvatarPipeline(), [abortAvatarPipeline]);

  useEffect(() => {
    if (!visible) return;
    const nextDraft = createDraft(profile);
    setDraft(nextDraft);
    setAvatarPreview(nextDraft.avatar);
    setBackgroundPreview(nextDraft.background);
    setBackgroundVideoPreview(nextDraft.backgroundVideo || '');
    setAvatarFrameSource('');
    setGeneratedAssetUrls(null);
  }, [profile, visible]);

  const onPickAvatar: ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (isPreset) return;
    const file = event.target.files?.[0];
    if (!file) return;
    // Start a fresh abort scope for this pick; cancel/close aborts `signal`.
    abortAvatarPipeline();
    const controller = new AbortController();
    avatarPipelineAbortRef.current = controller;
    const { signal } = controller;
    try {
      const raw = await readImageAsDataUrl(file);
      const normalized = await resizeImageDataUrl(raw, {
        maxEdge: 1440,
        mimeType: 'image/jpeg',
        quality: 0.9,
      });
      if (signal.aborted) return;
      setAvatarPreview(normalized);
      setAvatarProcessing(true);
      setAvatarStage('removing');
      // Single Ark pipeline: Ark's server-side semantic cutout regularizes the
      // framing (understands the scene, drops a stool the subject sits on, lands
      // the subject on a square-ish canvas with room) so the three generated
      // frames stay compositionally stable. All three frames are INDEPENDENT
      // one-gen Ark edits (includeNeutral=false) — we no longer derive the
      // mouth/eyes from a redrawn neutral (two generations of drift caused the
      // black-teeth / skewed-mouth artifacts). imgly only runs later as the
      // edge-matte step inside matteGeneratedFrame.
      const includeNeutral = false;
      const resp: { imageUrl?: string; imageBase64?: string; imageMimeType?: string } =
        await Apis.Basic.removeAvatarBg(
          {
            image: normalized,
            model: arkModel,
          },
          signal
        );
      if (signal.aborted) return;
      const processed = await resolveAvatarRemovalResult(resp, normalized);
      if (signal.aborted) return;
      setAvatarPreview(processed);
      setAvatarFrameSource(processed);
      setAvatarStage('uploading');
      const uploadedUrl = await uploadImageAsset(processed, 'avatar', signal);
      if (signal.aborted) return;
      setDraft((prev) => ({
        ...prev,
        avatar: uploadedUrl,
        live2dModelUrl: '',
        // A new avatar invalidates any previously generated animation frames.
        portraitFrames: undefined,
      }));
      Message.success('头像已上传到 TOS');

      // Auto-generate the paper-puppet frames (mouth-open / eyes-closed) so the
      // portrait can animate. Best-effort: a failure just leaves it static.
      try {
        setFramesGenerating(true);
        setAvatarStage('generating');
        // Choose the plate color from the matted subject's own colors: whichever
        // of green / magenta the subject is farthest from, so bg never collides
        // with the subject. Ark echoes the color back and the client keys it out.
        const keyColor = await pickMatteKeyColor(processed);
        // Ark generates the three frames off the regularized cutout. Pass the TOS
        // url first with the data URL as fallback.
        const frames = await generatePortraitFrames(
          includeNeutral,
          keyColor,
          arkModel,
          signal,
          uploadedUrl,
          processed
        );
        if (signal.aborted) return;
        if (frames?.mouthOpen || frames?.eyesClosed) {
          const nextAvatar = frames.neutral || uploadedUrl;
          setDraft((prev) => ({
            ...prev,
            // Adopt the normalized neutral Ark returns so the static portrait
            // matches the two animation frames exactly.
            avatar: nextAvatar,
            portraitFrames: {
              mouthOpen: frames.mouthOpen || '',
              eyesClosed: frames.eyesClosed || '',
            },
          }));
          if (frames.neutral) {
            setAvatarPreview(frames.neutral);
          }
          // Surface the final TOS URLs so a preset regeneration can be copied
          // back into useCompanions.ts (presets don't persist through save).
          setGeneratedAssetUrls({
            avatar: nextAvatar,
            mouthOpen: frames.mouthOpen || '',
            eyesClosed: frames.eyesClosed || '',
          });
          Message.success('已生成会动的立绘');
        } else {
          Message.warning('立绘动画帧生成失败，当前头像会先静态显示');
        }
      } catch (error) {
        if ((error as { name?: string })?.name === 'AbortError' || signal.aborted) return;
        Message.warning('立绘动画帧生成失败，当前头像会先静态显示');
      } finally {
        if (!signal.aborted) setFramesGenerating(false);
      }
    } catch (error) {
      // A user cancel is not a failure — stay silent.
      if ((error as { name?: string })?.name === 'AbortError' || signal.aborted) return;
      // The generic toast hides model/WASM fetch failures, so log the real cause.
      console.error('[avatar] pick/matte/upload failed:', error);
      Message.warning('头像处理或上传失败，请换一张试试');
    } finally {
      if (avatarPipelineAbortRef.current === controller) {
        avatarPipelineAbortRef.current = null;
      }
      if (!signal.aborted) {
        setAvatarProcessing(false);
        setAvatarStage('idle');
      }
      event.target.value = '';
    }
  };

  const onPickBackground: ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (isPreset) return;
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setBackgroundUploading(true);
      const raw = await readImageAsDataUrl(file);
      const normalized = await resizeImageDataUrl(raw, {
        maxEdge: 1920,
        mimeType: 'image/jpeg',
        quality: 0.86,
      });
      setBackgroundPreview(normalized);
      const uploadedUrl = await uploadImageAsset(normalized, 'background');
      setDraft((prev) => ({
        ...prev,
        background: uploadedUrl,
      }));
      Message.success('背景图已上传到 TOS');
    } catch (error) {
      Message.warning('背景图上传失败，请稍后重试');
    } finally {
      setBackgroundUploading(false);
      event.target.value = '';
    }
  };

  const captureVideoFirstFrame = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      video.src = url;
      const cleanup = () => URL.revokeObjectURL(url);
      video.onloadeddata = () => {
        try {
          video.currentTime = Math.min(0.1, (video.duration || 1) / 2);
        } catch (err) {
          cleanup();
          reject(err);
        }
      };
      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 1280;
          canvas.height = video.videoHeight || 720;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('canvas 2d unavailable');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          cleanup();
          resolve(canvas.toDataURL('image/jpeg', 0.86));
        } catch (err) {
          cleanup();
          reject(err);
        }
      };
      video.onerror = () => {
        cleanup();
        reject(new Error('video decode failed'));
      };
    });

  const onPickBackgroundVideo: ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (isPreset) return;
    const file = event.target.files?.[0];
    if (!file) return;
    // Guard client-side: the server only accepts mp4/webm up to 50MB.
    const isAllowedType = /video\/(mp4|webm)/i.test(file.type);
    if (!isAllowedType) {
      Message.warning('背景视频仅支持 mp4 / webm 格式');
      event.target.value = '';
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      Message.warning('背景视频请控制在 50MB 以内');
      event.target.value = '';
      return;
    }
    let localPreviewUrl = '';
    try {
      setBackgroundVideoUploading(true);
      // Local blob URL for instant preview; the uploaded TOS URL replaces it on success.
      localPreviewUrl = URL.createObjectURL(file);
      setBackgroundVideoPreview(localPreviewUrl);
      const dataUrl = await readImageAsDataUrl(file);
      const uploadedUrl = await uploadImageAsset(dataUrl, 'background-video');
      setDraft((prev) => ({
        ...prev,
        backgroundVideo: uploadedUrl,
      }));
      setBackgroundVideoPreview(uploadedUrl);
      Message.success('背景视频已上传到 TOS');
      // Mobile and preview-image fallbacks require a static poster, so when the
      // user hasn't picked a background image we synthesize one from the video's
      // first frame and upload it as the cover.
      if (!draft.background) {
        try {
          const frameDataUrl = await captureVideoFirstFrame(file);
          const resized = await resizeImageDataUrl(frameDataUrl, {
            maxEdge: 1920,
            mimeType: 'image/jpeg',
            quality: 0.86,
          });
          const coverUrl = await uploadImageAsset(resized, 'background');
          setBackgroundPreview(resized);
          setDraft((prev) => ({ ...prev, background: coverUrl }));
        } catch (frameError) {
          console.warn('capture video first frame failed', frameError);
        }
      }
    } catch (error) {
      Message.warning('背景视频上传失败，请稍后重试');
      setBackgroundVideoPreview(draft.backgroundVideo || '');
    } finally {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
      setBackgroundVideoUploading(false);
      event.target.value = '';
    }
  };

  const clearBackgroundVideo = () => {
    if (isPreset) return;
    setDraft((prev) => ({ ...prev, backgroundVideo: '' }));
    setBackgroundVideoPreview('');
  };

  const onPickBackgroundOrVideo: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (isPreset) return;
    const file = event.target.files?.[0];
    if (!file) return;
    if (/^video\//i.test(file.type)) {
      onPickBackgroundVideo(event);
    } else {
      onPickBackground(event);
    }
  };

  const handleConfirm = async () => {
    if (!draft.name.trim()) {
      Message.warning('请先填写角色名字');
      return;
    }
    try {
      setAssetPreparing(true);
      if (isPreset && profile) {
        await onSubmit(
          {
            ...createDraft(profile),
            name: draft.name,
            userCallName: draft.userCallName,
            personality: draft.personality,
            voiceType: draft.voiceType,
          },
          profile.id
        );
        return;
      }
      const avatar = isInlineAsset(draft.avatar)
        ? await uploadImageAsset(draft.avatar, 'avatar')
        : draft.avatar;
      const background = isInlineAsset(draft.background)
        ? await uploadImageAsset(draft.background, 'background')
        : draft.background;
      // Background video is uploaded eagerly on pick, so draft.backgroundVideo is
      // already a TOS URL; guard against a stale inline value just in case.
      const backgroundVideo =
        draft.backgroundVideo && isInlineAsset(draft.backgroundVideo)
          ? await uploadImageAsset(draft.backgroundVideo, 'background-video')
          : draft.backgroundVideo || '';
      let portraitFrames = draft.portraitFrames;
      let resolvedAvatar = avatar;
      const missingAnimationFrame = !portraitFrames?.mouthOpen || !portraitFrames?.eyesClosed;
      if (!isPreset && avatar && missingAnimationFrame) {
        try {
          setFramesGenerating(true);
          // Single Ark pipeline (same as the pick path): Ark generated three
          // independent one-gen edits off the regularized source — no
          // derive-from-neutral (includeNeutral stays false). Ark paints them on
          // a green/magenta plate the client color-keys inside matteGeneratedFrame.
          // Pick the plate off the matted source when we still have it (falls back
          // to green if the avatar is a remote URL and can't be sampled).
          const keyColor = await pickMatteKeyColor(avatarFrameSource || avatar);
          const generatedFrames = await generatePortraitFrames(
            false,
            keyColor,
            arkModel,
            undefined,
            avatar,
            avatarFrameSource
          );
          portraitFrames = {
            mouthOpen: portraitFrames?.mouthOpen || generatedFrames?.mouthOpen || '',
            eyesClosed: portraitFrames?.eyesClosed || generatedFrames?.eyesClosed || '',
          };
          // Adopt the Ark-redrawn neutral as the avatar so the static portrait
          // shares the frames' render domain.
          if (generatedFrames?.neutral) {
            resolvedAvatar = generatedFrames.neutral;
          }
          if (portraitFrames.mouthOpen || portraitFrames.eyesClosed) {
            Message.success('已补生成会动的立绘');
          } else {
            portraitFrames = undefined;
            Message.warning('立绘动画帧生成失败，当前头像会先静态显示');
          }
        } catch {
          Message.warning('立绘动画帧生成失败，当前头像会先静态显示');
        } finally {
          setFramesGenerating(false);
        }
      }
      const nextDraft = {
        ...draft,
        avatar: resolvedAvatar,
        background,
        backgroundVideo,
        portraitFrames,
      };
      setDraft(nextDraft);
      await onSubmit(nextDraft, profile?.id);
    } catch (error) {
      Message.warning('图片迁移到 TOS 失败，请稍后重试');
    } finally {
      setAssetPreparing(false);
    }
  };

  const handleCancel = useCallback(() => {
    // Truly cancel: interrupt the in-flight avatar pipeline (bg-remove → upload →
    // frame gen) and reset its progress flags before closing the window.
    abortAvatarPipeline();
    setAvatarProcessing(false);
    setAvatarStage('idle');
    setFramesGenerating(false);
    onCancel();
  }, [abortAvatarPipeline, onCancel]);

  return (
    <Modal
      visible={visible}
      style={{ width: 780 }}
      className={styles.modal}
      title={profile ? '编辑伙伴' : '创建伙伴'}
      onCancel={handleCancel}
      onOk={handleConfirm}
      confirmLoading={
        submitting ||
        avatarProcessing ||
        framesGenerating ||
        backgroundUploading ||
        backgroundVideoUploading ||
        assetPreparing
      }
      okText={profile ? '保存' : '创建'}
      cancelText="取消"
    >
      <div className={styles.modalBody}>
        <div className={styles.leftCol}>
          <div
            className={`${styles.stage} ${!avatarPreview && !isPreset ? styles.stageEmpty : ''}`}
            onClick={(e) => {
              if (isPreset) return;
              if ((e.target as HTMLElement).closest(`.${styles.stageModelToggle}`)) return;
              if (avatarStage === 'idle') avatarInputRef.current?.click();
            }}
            role={isPreset ? 'img' : 'button'}
            aria-label={isPreset ? '内置角色形象预览' : '上传或更换头像'}
            tabIndex={isPreset ? undefined : 0}
          >
            {avatarPreview && backgroundVideoPreview ? (
              <video
                className={styles.stageBg}
                src={backgroundVideoPreview}
                muted
                loop
                playsInline
                autoPlay
              />
            ) : avatarPreview && backgroundPreview ? (
              <img className={styles.stageBg} src={backgroundPreview} alt="background preview" />
            ) : null}
            {avatarPreview ? (
              <img className={styles.stageAvatar} src={avatarPreview} alt="avatar preview" />
            ) : (
              <div className={styles.stagePlaceholder}>
                <div className={styles.uploadIcon} aria-hidden>
                  <svg width="42" height="42" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 16V5m0 0l-4 4m4-4l4 4"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4 15v2a3 3 0 003 3h10a3 3 0 003-3v-2"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className={styles.stagePlaceholderText}>点击上传头像</div>
                <div className={styles.stagePlaceholderHint}>支持 JPG / PNG，建议正面清晰照</div>
              </div>
            )}
            {!isPreset ? (
              <div
                className={`${styles.stageModelToggle} ${styles.modelToggleBtns}`}
                onClick={(e) => e.stopPropagation()}
                title="选择下次上传或更换头像时使用的模型"
              >
                <button
                  type="button"
                  className={`${styles.modelBtn} ${!useProModel ? styles.modelBtnActive : ''}`}
                  onClick={() => setUseProModel(false)}
                >
                  Flash
                </button>
                <button
                  type="button"
                  className={`${styles.modelBtn} ${useProModel ? styles.modelBtnActive : ''}`}
                  onClick={() => setUseProModel(true)}
                >
                  Pro
                </button>
              </div>
            ) : null}
            {!isPreset &&
            avatarPreview &&
            avatarStage === 'idle' &&
            !framesGenerating &&
            !avatarProcessing ? (
              <div className={styles.stageBgActions} onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className={styles.stageIconBtn}
                  onClick={() => avatarInputRef.current?.click()}
                  title="更换头像"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="8.5" r="3.5" stroke="currentColor" strokeWidth="1.6" />
                    <path
                      d="M4.5 20c1-3.5 4-5.5 7.5-5.5s6.5 2 7.5 5.5"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span>更换头像</span>
                </button>
                <button
                  type="button"
                  className={styles.stageIconBtn}
                  onClick={() => bgAnyInputRef.current?.click()}
                  disabled={backgroundUploading || backgroundVideoUploading}
                  title={backgroundPreview || draft.backgroundVideo ? '更换背景' : '添加背景'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="3.5"
                      y="4.5"
                      width="17"
                      height="13"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                    <circle cx="8.5" cy="9.5" r="1.5" fill="currentColor" />
                    <path
                      d="M4 16l5-4 4 3 3-2 4 3"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                  <span>
                    {backgroundUploading || backgroundVideoUploading
                      ? '上传中'
                      : backgroundPreview || draft.backgroundVideo
                      ? '更换背景'
                      : '添加背景'}
                  </span>
                </button>
                {(backgroundPreview || draft.backgroundVideo) &&
                !backgroundUploading &&
                !backgroundVideoUploading ? (
                  <button
                    type="button"
                    className={`${styles.stageIconBtn} ${styles.stageIconBtnDanger}`}
                    aria-label="移除背景"
                    onClick={() => {
                      if (draft.backgroundVideo) clearBackgroundVideo();
                      setBackgroundPreview('');
                      setDraft((prev) => ({ ...prev, background: '' }));
                    }}
                    title="移除背景"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M6 6l12 12M18 6L6 18"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                ) : null}
              </div>
            ) : null}
            {avatarStage !== 'idle' ? (
              <div className={styles.stageOverlay}>{AVATAR_STAGE_LABEL[avatarStage]}</div>
            ) : framesGenerating ? (
              <div className={styles.stageOverlay}>生成立绘帧中...</div>
            ) : null}
          </div>

          {profile?.id === 'preset_hiyori' ? (
            <div className={styles.fieldHint} style={{ textAlign: 'center' }}>
              Hiyori 使用 Live2D 生成
            </div>
          ) : null}
          {draft.portraitFrames?.mouthOpen || draft.portraitFrames?.eyesClosed ? (
            <div className={styles.framesRow}>
              {[
                { label: '静态', src: avatarPreview },
                { label: '张嘴', src: draft.portraitFrames?.mouthOpen },
                { label: '闭眼', src: draft.portraitFrames?.eyesClosed },
              ].map((item) => (
                <div className={styles.frameItem} key={item.label}>
                  {item.src ? <img src={item.src} alt={item.label} /> : null}
                  {item.src ? <span>{item.label}</span> : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className={styles.rightCol}>
          <div className={styles.field}>
            <div className={styles.label}>名字 *</div>
            <Input
              value={draft.name}
              onChange={(value) => setDraft((prev) => ({ ...prev, name: value }))}
              placeholder="例如：小宁 / 阿柯 / 你的专属助手"
            />
          </div>
          <div className={styles.field}>
            <div className={styles.label}>你怎么称呼</div>
            <Input
              value={draft.userCallName}
              onChange={(value) => setDraft((prev) => ({ ...prev, userCallName: value }))}
              placeholder="例如：小周 / 宝宝 / 直接叫我名字"
            />
          </div>
          <div className={styles.field}>
            <div className={styles.label}>音色</div>
            <Select
              value={draft.voiceType}
              onChange={(value) => setDraft((prev) => ({ ...prev, voiceType: value }))}
              placeholder="选择音色"
              triggerProps={{ className: 'companion-modal-select-dropdown' }}
              dropdownMenuClassName="companion-modal-voice-list"
              dropdownMenuStyle={{ overflowX: 'hidden' }}
            >
              {VOICE_LIST.map((voice) => (
                <Select.Option key={voice.value} value={voice.value}>
                  {voice.label}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className={styles.field}>
            <div className={styles.label}>人格</div>
            <Input.TextArea
              value={draft.personality}
              onChange={(value) => setDraft((prev) => ({ ...prev, personality: value }))}
              placeholder="例如：加州理工理论物理博士，擅长高中数学、物理竞赛和几何证明；讲解严谨、循序渐进。"
              autoSize={false}
              rows={6}
              style={{ resize: 'none', overflowY: 'auto' }}
            />
          </div>
          <div className={styles.fieldHint}>
            背景支持图片（JPG / PNG / WebP）或视频（MP4 / WebM，50MB
            以内）。桌面端的沉浸模式待机页与通话背景会播放视频；移动端或视频无法播放时显示背景图片。仅上传视频时，会自动截取首帧作为备用背景。
          </div>
        </div>

        <input
          ref={avatarInputRef}
          type="file"
          accept="image/*"
          disabled={isPreset}
          onChange={onPickAvatar}
          style={{ display: 'none' }}
        />
        <input
          ref={bgAnyInputRef}
          type="file"
          accept="image/*,video/mp4,video/webm"
          disabled={isPreset}
          onChange={onPickBackgroundOrVideo}
          style={{ display: 'none' }}
        />
        <input
          ref={bgInputRef}
          type="file"
          accept="image/*"
          disabled={isPreset}
          onChange={onPickBackground}
          style={{ display: 'none' }}
        />
        <input
          ref={bgVideoInputRef}
          type="file"
          accept="video/mp4,video/webm"
          disabled={isPreset}
          onChange={onPickBackgroundVideo}
          style={{ display: 'none' }}
        />
      </div>
    </Modal>
  );
}
